import Vue from 'vue';
import mixpanel from 'mixpanel-browser';
import TrackingService from '@animaapp/anima-tracking';
import store from '@/store';
import { addDebugEvent } from '@/services/bus';
import errorHandler from '@/services/errorHandler';
import { isDev } from '@/utils/environment';
import { hasAnalyticsConsent } from '@/utils/consent';
import { syncMixpanelConsent } from '@/services/mixpanelConsent';
import { guardDispatchers } from '@/services/trackingConsent';

const config = {
  web: true,
  clientId: 'com.animaapp.web',
  baseURL: process.env.TRACKING_BASE_URL
};

/*
 * The service instance is exposed as Vue.prototype.$tracking, and views call it
 * directly: Root.vue, Login.vue, LoginWithSSO.vue and Signup.vue all call
 * alias(), which POSTs the user id and the visitor id to
 * /analytics/register_alias. Guarding the two wrappers below therefore was not
 * enough - anything holding the raw service walked straight past them.
 *
 * The methods that dispatch are guarded on the instance itself, so every caller
 * is covered however it reached the service.
 *
 * The identity setters are deliberately left working. They only populate local
 * state, and keeping them means the service is ready to send correctly the
 * moment somebody accepts, rather than reporting an anonymous session.
 */
export const tracking = guardDispatchers(new TrackingService(config));
export const trackEvent = (eventName, params = {}) => {
  if (!eventName) {
    return;
  } else if (localStorage.getItem('animp')) {
    // if impersonating - don't track
    return;
  } else if (!hasAnalyticsConsent()) {
    // Non-essential analytics: nothing leaves the browser until the visitor
    // agrees, the same rule the GTM container and tolt.js already follow.
    return;
  }

  const extraTrackingData = store.getters['tracking/extraData'];
  const isChromeExtension = store.state.omniview?.isChromeExtension;

  if (isChromeExtension) {
    let extensionName = store.state['omniview'].extensionName;

    // add this flag so we can detect if this event is coming from the chrome extension or not
    params.extensionName = extensionName;
    params.fromChromeExtension = true;
  }
  try {
    tracking.trackEvent(eventName, { ...extraTrackingData, ...params });
    Vue.prototype?.$hj && Vue.prototype?.$hj('event', eventName);
  } catch (err) {
    errorHandler.captureException(err);
  }

  addDebugEvent({ eventName, params: { ...extraTrackingData, ...params } });
};

export const trackUserProps = (props) => {
  // User properties are the most identifying thing we send, so they wait for
  // consent like everything else - and for the same impersonation check
  // trackEvent already makes, which this had been missing.
  if (!hasAnalyticsConsent() || localStorage.getItem('animp')) {
    return;
  }

  try {
    tracking.setUserProperties(props);
  } catch (err) {
    errorHandler.captureException(err);
  }
};

export const debugEvent = (eventName, params = {}) => {
  const extraTrackingData = store.getters['tracking/extraData'];
  addDebugEvent({ eventName, params: { ...extraTrackingData, ...params } });
};

// remove timeout until refactor for better measuring
export const measure = (label) => tracking.measureStart(`web-app.${label}`);
export const measureEnd = (label, props) => tracking.measureEnd(`web-app.${label}`, props);

/*
 * Mixpanel starts opted out, so nothing is stored or sent until the visitor
 * agrees. init() on its own makes no network call, but it does write a
 * tracking cookie, and identify()/people.set() in Root.vue post the user id and
 * IP address to api-js.mixpanel.com - so gating only the calls would still
 * leave the cookie. These two flags cover the cookie and every dispatch,
 * including any call site added later.
 */
export const initMixpanel = () => {
  const options = {
    debug: isDev(),
    opt_out_tracking_by_default: true,
    opt_out_persistence_by_default: true
  };
  mixpanel.init(process.env.MIXPANEL_TOKEN, options);

  syncMixpanelConsent();
};
